import React from "react";

const DIAS_ENC = [
  ["segunda", "Seg"], ["terca", "Ter"], ["quarta", "Qua"],
  ["quinta", "Qui"], ["sexta", "Sex"], ["sabado", "Sáb"], ["domingo", "Dom"],
];

export default function ProdutoFormAbaEstoque({ data, update }) {
  const est = data.estoque || { tipo: "sempre_disponivel" };
  const enc = data.encomenda_config || null;
  const tipoDisp = data.tipo_disponibilidade || "pronta_entrega";

  const setEst = (patch) => update({ estoque: { ...est, ...patch } });

  const setEnc = (patch) => {
    if (!enc) {
      update({
        encomenda_config: {
          ativo: true, aceita_encomenda: true,
          antecedencia_minima_dias: 2, janela_futura_dias: 14,
          dias_entrega: ["todos"], observacao_cliente: "",
          ...patch,
        },
      });
    } else {
      update({ encomenda_config: { ...enc, ...patch } });
    }
  };

  const setTipoDisp = (tipo) => {
    if (tipo === "pronta_entrega") {
      update({ tipo_disponibilidade: tipo });
    } else {
      // Garante encomenda_config existente quando ativa encomendas
      const next = enc || {
        ativo: true, aceita_encomenda: true,
        antecedencia_minima_dias: 2, janela_futura_dias: 14,
        dias_entrega: ["todos"], observacao_cliente: "",
      };
      update({ tipo_disponibilidade: tipo, encomenda_config: next });
    }
  };

  const toggleDia = (dia) => {
    const dias = (enc && enc.dias_entrega) || ["todos"];
    if (dia === "todos") {
      setEnc({ dias_entrega: ["todos"] });
      return;
    }
    // Remove "todos" e alterna o dia
    const semTodos = dias.filter((d) => d !== "todos");
    const next = semTodos.includes(dia)
      ? semTodos.filter((d) => d !== dia)
      : [...semTodos, dia];
    setEnc({ dias_entrega: next.length ? next : ["todos"] });
  };

  return (
    <div className="space-y-5" data-testid="aba-estoque">
      {/* ESTOQUE */}
      <div>
        <p className="text-[11px] uppercase tracking-wide text-[var(--text-secondary)] font-semibold mb-2">
          Controle de estoque
        </p>
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="est-tipo"
              checked={est.tipo === "sempre_disponivel"}
              onChange={() => setEst({ tipo: "sempre_disponivel" })}
              data-testid="estoque-sempre" />
            <div>
              <span className="font-medium">Sempre disponível</span>
              <p className="text-xs text-[var(--text-secondary)]">Sem controle de quantidade.</p>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="est-tipo"
              checked={est.tipo === "limitado"}
              onChange={() => setEst({ tipo: "limitado" })}
              data-testid="estoque-limitado" />
            <div>
              <span className="font-medium">Quantidade limitada</span>
              <p className="text-xs text-[var(--text-secondary)]">Conta unidades; alerta + bloqueio configurável.</p>
            </div>
          </label>
        </div>

        {est.tipo === "limitado" && (
          <div className="mt-3 space-y-2 pl-5 border-l-2 border-orange-200">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium mb-1">Quantidade</label>
                <input type="number" min="0" value={est.quantidade ?? 0}
                  onChange={(e) => setEst({ quantidade: Math.max(0, parseInt(e.target.value || 0, 10)) })}
                  className="w-full px-2 py-1.5 border border-[var(--border)] rounded text-sm"
                  data-testid="estoque-quantidade" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Alertar quando restar</label>
                <input type="number" min="0" value={est.alerta_minimo ?? 0}
                  onChange={(e) => setEst({ alerta_minimo: Math.max(0, parseInt(e.target.value || 0, 10)) })}
                  className="w-full px-2 py-1.5 border border-[var(--border)] rounded text-sm"
                  data-testid="estoque-alerta" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Ao zerar</label>
              <div className="flex flex-col gap-1 text-xs">
                <label className="flex items-center gap-1.5">
                  <input type="radio" name="ao-zerar"
                    checked={est.ao_zerar === "bloquear_automatico"}
                    onChange={() => setEst({ ao_zerar: "bloquear_automatico" })} />
                  Ocultar do cardápio automaticamente
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" name="ao-zerar"
                    checked={est.ao_zerar === "mostrar_esgotado"}
                    onChange={() => setEst({ ao_zerar: "mostrar_esgotado" })} />
                  Mostrar como "Esgotado"
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ENCOMENDA */}
      <div className="pt-3 border-t border-[var(--border)]">
        <p className="text-[11px] uppercase tracking-wide text-[var(--text-secondary)] font-semibold mb-2">
          Tipo de venda
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            ["pronta_entrega", "Somente pronta entrega"],
            ["encomenda", "Somente encomenda"],
            ["ambos", "Pronta entrega e encomenda"],
          ].map(([v, l]) => (
            <button key={v} type="button"
              onClick={() => setTipoDisp(v)}
              data-testid={`tipo-disp-${v}`}
              className={`px-2 py-2 text-xs rounded-lg border text-left ${
                tipoDisp === v
                  ? "border-orange-500 bg-orange-50 text-orange-700 font-semibold"
                  : "border-[var(--border)] hover:bg-[var(--bg-secondary)]"
              }`}>{l}</button>
          ))}
        </div>

        {tipoDisp !== "pronta_entrega" && (
          <div className="mt-3 space-y-2 pl-5 border-l-2 border-orange-200">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox"
                checked={!!(enc && enc.aceita_encomenda)}
                onChange={(e) => setEnc({ aceita_encomenda: e.target.checked, ativo: true })}
                data-testid="encomenda-aceita" />
              Aceitar encomendas agora
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium mb-1">Antecedência mínima (dias)</label>
                <input type="number" min="0" max="60"
                  value={(enc && enc.antecedencia_minima_dias) ?? 2}
                  onChange={(e) => setEnc({ antecedencia_minima_dias: Math.max(0, parseInt(e.target.value || 0, 10)) })}
                  className="w-full px-2 py-1.5 border border-[var(--border)] rounded text-sm"
                  data-testid="encomenda-antecedencia" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Janela (dias à frente)</label>
                <input type="number" min="1" max="90"
                  value={(enc && enc.janela_futura_dias) ?? 14}
                  onChange={(e) => setEnc({ janela_futura_dias: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                  className="w-full px-2 py-1.5 border border-[var(--border)] rounded text-sm"
                  data-testid="encomenda-janela" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Dias de entrega</label>
              <div className="flex flex-wrap gap-1">
                <label className={`px-2 py-1 text-xs rounded border cursor-pointer ${
                  (enc?.dias_entrega || ["todos"]).includes("todos")
                    ? "bg-orange-500 text-white border-orange-500"
                    : "border-[var(--border)]"
                }`}>
                  <input type="checkbox" className="hidden"
                    checked={(enc?.dias_entrega || ["todos"]).includes("todos")}
                    onChange={() => toggleDia("todos")} />
                  Todos
                </label>
                {DIAS_ENC.map(([k, l]) => (
                  <label key={k} className={`px-2 py-1 text-xs rounded border cursor-pointer ${
                    (enc?.dias_entrega || []).includes(k)
                      ? "bg-orange-500 text-white border-orange-500"
                      : "border-[var(--border)]"
                  }`}>
                    <input type="checkbox" className="hidden"
                      checked={(enc?.dias_entrega || []).includes(k)}
                      onChange={() => toggleDia(k)} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Observação para o cliente</label>
              <textarea rows={2}
                value={(enc && enc.observacao_cliente) || ""}
                onChange={(e) => setEnc({ observacao_cliente: e.target.value.slice(0, 500) })}
                placeholder='Ex: "Encomendas fechadas toda quinta até 18h"'
                className="w-full px-2 py-1.5 border border-[var(--border)] rounded text-xs"
                data-testid="encomenda-observacao" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
